// Ad Preview Stage 3 — eligibility + grouping helper.
//
// Multi-Location Amendment §A1 + §A5.2: every send batch aggregates
// one message per contact per channel, regardless of how many rows
// the contact qualifies for. This helper is the single source of
// truth for who-gets-what -- the orchestrator (Phase D) feeds it
// triples of `{row, previewLink, contact}` plus the channels being
// sent, and gets back a per-channel map of contact-grouped sends.
//
// Pure. No firestore-admin, no IO, no time. Easy to unit-test;
// reused by both the server-side bulk orchestrator and the
// client-side modal that previews the aggregate counts.
//
// The two gates:
//
//   1. Per-link `receivesCampaignPreviews`. The contact must have an
//      AccountLink to the row's location AND that link must opt in
//      to campaign previews. AMs can opt John in for Snap Brisbane
//      but out for Snap Sydney -- this is the granular gate.
//
//   2. Per-channel contact-level. `contact.channelEmail` + non-empty
//      email for the email channel; `contact.channelSMS` + non-empty
//      phone for SMS. Backstops the per-link check: a contact opted
//      out of email globally doesn't receive email even if the link
//      is on.
//
// Both gates AND together. The Stage 1 spec's `preferences` object
// wording (which would have been a contact-level flag) was
// superseded by amendment §A5.2 explicitly using the per-link
// `receivesCampaignPreviews` field.

import type { PreviewSendChannel } from '../types/previewSend.js';

// Structural types -- callers pass the minimum shape, both server
// `StoredX` types and wire types satisfy these. Avoids coupling the
// helper to either.

export type EligibilityRow = {
  /** The row's location accountId (P2b: AccountCampaign.accountId). */
  accountId: string;
};

export type EligibilityPreviewLink = {
  id: string;
};

export type EligibilityContact = {
  id: string;
  email: string | null;
  phone: string | null;
  channelEmail: boolean;
  channelSMS: boolean;
  accountLinks: ReadonlyArray<{
    accountId: string;
    receivesCampaignPreviews: boolean;
  }>;
};

// A pre-resolved (row × previewLink × contact) triple. Caller is
// responsible for enumerating rows × their linked contacts; this
// helper only knows how to filter + group.
export type EligibilityTriple<
  R extends EligibilityRow = EligibilityRow,
  P extends EligibilityPreviewLink = EligibilityPreviewLink,
  C extends EligibilityContact = EligibilityContact,
> = {
  row: R;
  previewLink: P;
  contact: C;
};

// One contact's aggregate send across all the rows they qualify for
// in this batch. `rows` + `previewLinks` are positionally aligned --
// index i in `rows` corresponds to index i in `previewLinks` -- so
// the template helper can map across them in lockstep.
export type ContactSendGroup<
  R extends EligibilityRow = EligibilityRow,
  P extends EligibilityPreviewLink = EligibilityPreviewLink,
  C extends EligibilityContact = EligibilityContact,
> = {
  contact: C;
  rows: R[];
  previewLinks: P[];
};

/**
 * Apply the per-link `receivesCampaignPreviews` gate and group by
 * contactId. The per-channel filter is NOT applied here -- callers
 * use {@link filterGroupForChannel} or {@link getEligibleContactSends}
 * for the channel-specific cut.
 *
 * A contact can appear in multiple triples (because they're linked to
 * multiple rows in the batch). Each surviving triple contributes one
 * entry to that contact's `rows` + `previewLinks` arrays. Within a
 * single contact's group, rows preserve insertion order so the
 * downstream UI can render them in whatever order the orchestrator
 * fed in (typically location-name alphabetical).
 *
 * De-duplication: a triple with a (contact, row) pair already in the
 * group is dropped silently -- this protects against the caller
 * feeding the same row twice (e.g. a buggy orchestrator). The first
 * occurrence wins.
 */
export function groupSendsByContact<
  R extends EligibilityRow,
  P extends EligibilityPreviewLink,
  C extends EligibilityContact,
>(triples: ReadonlyArray<EligibilityTriple<R, P, C>>): Map<string, ContactSendGroup<R, P, C>> {
  const grouped = new Map<string, ContactSendGroup<R, P, C>>();
  // Per-group de-dup keyed by `${contactId}::${row.accountId}::${previewLink.id}`.
  // Cheap to compute, catches the realistic dup shape.
  const seen = new Set<string>();

  for (const { row, previewLink, contact } of triples) {
    const link = contact.accountLinks.find((l) => l.accountId === row.accountId);
    if (!link || !link.receivesCampaignPreviews) continue;

    const dedupKey = `${contact.id}::${row.accountId}::${previewLink.id}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);

    let group = grouped.get(contact.id);
    if (!group) {
      group = { contact, rows: [], previewLinks: [] };
      grouped.set(contact.id, group);
    }
    group.rows.push(row);
    group.previewLinks.push(previewLink);
  }

  return grouped;
}

/**
 * True when the contact has opted in to the channel AND the
 * corresponding contact field is non-empty. Pure per-contact check;
 * does not look at the row / preview link.
 */
export function contactEligibleForChannel(
  contact: EligibilityContact,
  channel: PreviewSendChannel,
): boolean {
  if (channel === 'email') {
    return contact.channelEmail && contact.email !== null && contact.email !== '';
  }
  return contact.channelSMS && contact.phone !== null && contact.phone !== '';
}

/**
 * Combined helper: applies both gates (per-link + per-channel) and
 * returns a per-channel map of contact-grouped sends. The output map
 * is keyed by channel, each entry keyed by contactId.
 *
 * `channels` controls which channels appear in the output. Channels
 * the caller doesn't pass don't get a map entry at all (not even an
 * empty map) -- the orchestrator usually iterates Object.entries on
 * the result and shouldn't produce no-op work for channels the AM
 * didn't tick.
 */
export function getEligibleContactSends<
  R extends EligibilityRow,
  P extends EligibilityPreviewLink,
  C extends EligibilityContact,
>(
  triples: ReadonlyArray<EligibilityTriple<R, P, C>>,
  channels: ReadonlyArray<PreviewSendChannel>,
): Record<PreviewSendChannel, Map<string, ContactSendGroup<R, P, C>>> {
  const linkGated = groupSendsByContact(triples);
  const out: Record<PreviewSendChannel, Map<string, ContactSendGroup<R, P, C>>> = {
    email: new Map(),
    sms: new Map(),
  };

  for (const channel of channels) {
    const channelMap = out[channel];
    for (const [contactId, group] of linkGated) {
      if (contactEligibleForChannel(group.contact, channel)) {
        channelMap.set(contactId, group);
      }
    }
  }

  // For channels the caller didn't pass, clear the default empty map
  // back out so consumers can rely on `channel in result` reflecting
  // the AM's selection. (Object.keys still returns 'email' + 'sms' on
  // the resulting record either way, but the maps are empty.)
  // Leaving them as empty Map is fine -- callers iterate Object.entries
  // and naturally do nothing on an empty map.
  return out;
}

/**
 * Convenience: the total send count across all channels.
 * `email recipients + sms recipients` (a contact on both channels
 * counts twice -- matches spec §3.2 "Total message count = email
 * recipients + SMS recipients").
 */
export function countEligibleSends(
  perChannel: Record<PreviewSendChannel, Map<string, unknown>>,
  channels: ReadonlyArray<PreviewSendChannel>,
): number {
  return channels.reduce((sum, c) => sum + perChannel[c].size, 0);
}
